// Shared UTC → viewer-local time formatting helper.
//
// Every timestamp column the backend writes (created_at, requested_at,
// decided_at, start_time, payment_date, etc.) is stored as a UTC wall-clock
// string with no timezone marker — the backend always does
// moment.utc().format("YYYY-MM-DD HH:mm:ss") (see e.g. models/focModel.js).
//
// dayjs(value) / moment(value) on a string with no 'Z'/offset treats it as
// ALREADY being in the browser's local time — no conversion happens — so a
// viewer in India (UTC+5:30) sees times running ~5.5 hours behind their real
// local time. The fix is to explicitly parse as UTC, then convert:
// dayjs.utc(value).local().
//
// Use formatLocalTime/formatLocalDateTime anywhere a *stored* timestamp with
// a time-of-day component is displayed — screens, Excel exports, PDFs.
// Don't use these for pure DATE columns (order_date, due_date, etc.) that
// have no time-of-day — those aren't affected by this bug, and running them
// through a timezone conversion risks shifting the calendar date near a UTC
// day boundary for no reason.

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

/**
 * Format a stored UTC timestamp string as the viewer's local time only.
 * @param {string|null|undefined} value - UTC timestamp from the backend
 * @param {string} [fmt="hh:mm A"] - dayjs format string
 * @returns {string} formatted local time, or "-" for a missing value
 */
export const formatLocalTime = (value, fmt = "hh:mm A") =>
  value ? dayjs.utc(value).local().format(fmt) : "-";

/**
 * Format a stored UTC timestamp string as the viewer's local date + time.
 * @param {string|null|undefined} value - UTC timestamp from the backend
 * @param {string} [fmt="DD-MM-YYYY hh:mm A"] - dayjs format string
 * @returns {string} formatted local date/time, or "-" for a missing value
 */
export const formatLocalDateTime = (value, fmt = "DD-MM-YYYY hh:mm A") =>
  value ? dayjs.utc(value).local().format(fmt) : "-";
